import Phaser from "phaser";
import { COLORS, GAME_HEIGHT, GAME_WIDTH } from "../config";
import type { RunResult } from "../types";

/** Calm closing beat: the region heals red → green, one affirmation, a tally. */
export default class LevelCompleteScene extends Phaser.Scene {
  constructor() {
    super("LevelComplete");
  }

  create(data: { result?: RunResult }): void {
    const result: RunResult = data.result ?? {
      region: "Lungs",
      pointsEarned: 0,
      enemiesCleared: 0,
      statLabel: "Final strength",
      statValue: 0,
    };

    const g = this.add.graphics();
    g.fillGradientStyle(COLORS.bgTop, COLORS.bgTop, COLORS.bgBottom, COLORS.bgBottom, 1);
    g.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    this.cameras.main.fadeIn(500, 5, 12, 26);

    const cx = GAME_WIDTH / 2;

    // Lungs icon, healing from inflamed red to a cool green.
    const lungs = this.add.graphics().setPosition(cx, GAME_HEIGHT * 0.32);
    const drawLungs = (color: number) => {
      lungs.clear();
      lungs.fillStyle(color, 0.9);
      lungs.fillEllipse(-34, 0, 60, 96);
      lungs.fillEllipse(34, 0, 60, 96);
    };
    drawLungs(COLORS.inflamed);
    const tintObj = { t: 0 };
    this.tweens.add({
      targets: tintObj,
      t: 1,
      duration: 1200,
      delay: 300,
      ease: "Sine.easeInOut",
      onUpdate: () => {
        const color = Phaser.Display.Color.Interpolate.ColorWithColor(
          Phaser.Display.Color.IntegerToColor(COLORS.inflamed),
          Phaser.Display.Color.IntegerToColor(COLORS.affirm),
          100,
          Math.floor(tintObj.t * 100),
        );
        drawLungs(Phaser.Display.Color.GetColor(color.r, color.g, color.b));
      },
    });

    this.add
      .text(cx, GAME_HEIGHT * 0.5, `Your ${result.region.toLowerCase()} feel clearer.`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "34px",
        color: "#bdf0d0",
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    this.add
      .text(
        cx,
        GAME_HEIGHT * 0.6,
        `+${result.pointsEarned} points\n` +
          `${result.enemiesCleared} cleared   ·   ${result.statLabel.toLowerCase()} ${result.statValue}`,
        {
          fontFamily: "Arial, sans-serif",
          fontSize: "26px",
          color: "#cfe8ff",
          align: "center",
          lineSpacing: 10,
        },
      )
      .setOrigin(0.5);

    this.makeButton(cx, GAME_HEIGHT * 0.8, "Return home", () => {
      this.cameras.main.fadeOut(350, 5, 12, 26);
      this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
        this.scene.start("Menu");
      });
    });
  }

  private makeButton(x: number, y: number, label: string, onClick: () => void): void {
    const bg = this.add
      .rectangle(x, y, 320, 64, 0x1f4f7a, 1)
      .setStrokeStyle(2, 0x9fd8ff, 0.7)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(x, y, label, {
        fontFamily: "Arial, sans-serif",
        fontSize: "26px",
        color: "#eaf6ff",
      })
      .setOrigin(0.5);
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, onClick);
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, () => bg.setFillStyle(0x2a6699));
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, () => bg.setFillStyle(0x1f4f7a));
  }
}
